//#region [IMPORT]

// === THIRD-PARTY ===
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// ### STYLES ###
import { useTheme } from '@mui/material/styles';
import { Box, TextField, Button, Link, InputAdornment, IconButton } from '@mui/material';
import {Visibility, VisibilityOff } from '@mui/icons-material';

// === API ===
import { prokeyApi } from 'api';

//#endregion

//-----------------------------------------------------------------
export function LoginForm() {
  //-----------------------------------------------------------------
  
  //#region [VALUES]
  
  const theme    = useTheme();
  const navigate = useNavigate();

  const [ username          , setUsername          ] = useState( ''    );
  const [ password          , setPassword          ] = useState( ''    );
  const [ showPassword      , setShowPassword      ] = useState( false );
  const [ showForgotPass    , setShowForgotPass    ] = useState( false );
  const [ showRequestAccess , setShowRequestAccess ] = useState( false );
  const [ ssoConfig         , setSsoConfig         ] = useState( { enabled: false, providerName: 'Single Sign-On' } );
  const [ samlConfig        , setSamlConfig        ] = useState( { enabled: false, providerName: 'SAML' } );
  const [ isError           , setIsError           ] = useState( false );

  let page = localStorage.getItem('display_page');
  if( !page ) page = 'verify';

  //#endregion

  //#region [STYLES]

  const _styles = {
    form: {      
      display       : 'flex'   ,
      flexDirection : 'column' ,
      alignItems    : 'center' ,
    },
    username: {
      width  : '100%'   ,
      margin : '0.5rem' ,
    },
    password: {
      width  : '100%'   ,
      margin : '0.5rem' ,
    },

    sign_in_container: {      
      width     : '100%'         ,
      marginTop : '0.75rem'      ,
      display   : 'inline-block' ,
    },
    forgot_link: {
      marginLeft: '1rem',
    },
    request_link: {
      marginLeft: '1rem',
    },

    alert: {
      display          : isError ? 'inline-block' : 'none',
      color            : 'white',
      backgroundColor  : 'darkred',
      marginTop        : '1rem',
      padding          : '1rem',
      width            : '100%',
      borderRadius     : '4px',
    },
    sso_button: {
      width     : '100%',
      marginTop : '0.75rem',
    }
  }

  const _props = {
    USERNAME: {
      name       : 'username_txt'   ,
      type       : 'username'       ,
      variant    : 'filled'         ,
      sx         : _styles.username ,
      label      : 'username'       ,
      value      : username         ,
      autoFocus  : true             ,
      onFocus    : clearAlert       ,
      onKeyPress : handleKeyPress   ,
      onChange   : (e) => setUsername(e.target.value),
    },
    PASSWORD: {
      name       : 'password_txt'   ,
      type       : showPassword ? 'test' : 'password',
      variant    : 'filled'         ,
      sx         : _styles.password ,
      label      : 'password'       ,
      value      : password         ,
      onFocus    : clearAlert       ,
      onKeyPress : handleKeyPress   ,
      onChange   : (e) => setPassword(e.target.value),
      InputProps : { 
        endAdornment: displayPasswordVisiblityButton(),
      }
    },

    FORGOT_LINK: {
      name      : 'forgot_link'   ,
      display   : showForgotPass ? '' : 'none',      
      component : 'button',
      sx        : _styles.forgot_link,
      onClick   : handleForgot,
      children  : ['Reset Password'],
    },
    REQUEST_LINK: {
      name      : 'request_link'   ,
      display   : showRequestAccess ? '' : 'none',      
      component : 'button',
      sx        : _styles.forgot_link,
      onClick   : handleRequest,
      children  : ['Request Access'],
    },
  }

  //#endregion

  //#region [EFFECT]

  useEffect( () => {
    //-----------------------------------------------------------------
    async function getForgotPass() {
      //-----------------------------------------------------------------
      try {
        const response = await prokeyApi.isPasswordResetEnabled();
        if( !response.data.enabled ) return;
        setShowForgotPass(true);
      } catch(error) {
        console.warn();
      }
    }

    //-----------------------------------------------------------------
    async function getRequestAccess() {
      //-----------------------------------------------------------------
      try {
        const response = await prokeyApi.getRequestAccess();
        if( !response.data.enabled ) return;
        setShowRequestAccess(true);
      } catch(error) {
        const {stack, ...rest} = error
        console.warn(rest);
      }
    }

    getForgotPass();
    // getRequestAccess();
    getSsoConfig();
    getSamlConfig();

  }, [])

  //#endregion

  //#region [EVENTS]
  
  //-----------------------------------------------------------------
  async function postLogin() {
    //-----------------------------------------------------------------
    try {
      setIsError(!username || !password);

      const result = await prokeyApi.login(username, password);

      if( result.status !== 200 ) {
        setIsError(true);
        return;
      }

      navigate( '/' + page );   // navigate to main page
      window.location.reload(); // reload page in case of code update
    } catch (error) {
      setIsError(true);
    }
  }

  //-----------------------------------------------------------------
  async function getSsoConfig() {
    //-----------------------------------------------------------------
    try {
      const response = await prokeyApi.getSsoConfig();
      setSsoConfig(response.data);
    } catch(error) {
      setSsoConfig({ enabled: false, providerName: 'Single Sign-On' });
    }
  }

  //-----------------------------------------------------------------
  async function getSamlConfig() {
    //-----------------------------------------------------------------
    try {
      const response = await prokeyApi.getSamlConfig();
      setSamlConfig(response.data);
    } catch(error) {
      setSamlConfig({ enabled: false, providerName: 'SAML' });
    }
  }

  //-----------------------------------------------------------------
  function startSsoLogin() {
    //-----------------------------------------------------------------
    prokeyApi.startSsoLogin();
  }

  //-----------------------------------------------------------------
  function startSamlLogin() {
    //-----------------------------------------------------------------
    prokeyApi.startSamlLogin();
  }

  //-----------------------------------------------------------------
  function handleKeyPress(target) {
    //-----------------------------------------------------------------
    // ENTER
    if (target.charCode === 13) postLogin();
  }

  //-----------------------------------------------------------------
  function handleForgot() {
    //-----------------------------------------------------------------
    navigate('forgot');
  }

  //-----------------------------------------------------------------
  function handleRequest() {
    //-----------------------------------------------------------------
    navigate('request-access');
  }

  //-----------------------------------------------------------------
  function handleClickShowPassword() {
    //-----------------------------------------------------------------
    setShowPassword(!showPassword);
  };

  //-----------------------------------------------------------------
  function handleMouseDownPassword(event) {
    //-----------------------------------------------------------------
    event.preventDefault();
  };

  //-----------------------------------------------------------------
  function clearAlert() {
    //-----------------------------------------------------------------
    setIsError(false);
  }

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <Box sx={ _styles.form }>
        <TextField {..._props.USERNAME} />
        <TextField {..._props.PASSWORD} />
        <Box sx={ _styles.sign_in_container }>
          <Link {..._props.FORGOT_LINK}  />
          <Link {..._props.REQUEST_LINK} />            
          <Button variant='contained' color='primary' sx={{ marginLeft: '1rem' }} onClick={postLogin} name='sign_in_btn'>
            Sign In
          </Button>
        </Box>
        {ssoConfig.enabled && (
          <Button variant='outlined' color='primary' sx={_styles.sso_button} onClick={startSsoLogin} name='sso_sign_in_btn'>
            Sign in with {ssoConfig.providerName}
          </Button>
        )}
        {samlConfig.enabled && (
          <Button variant='outlined' color='primary' sx={_styles.sso_button} onClick={startSamlLogin} name='saml_sign_in_btn'>
            Sign in with {samlConfig.providerName}
          </Button>
        )}
        <Box sx={ _styles.alert }>The username or password provided were incorrect!</Box>
      </Box>
    )
  }

  //-----------------------------------------------------------------
  function displayPasswordVisiblityButton() {
    //-----------------------------------------------------------------
    return (
      <InputAdornment position='end'>
        <IconButton
          aria-label  = 'toggle password visibility'
          onClick     = {handleClickShowPassword}
          onMouseDown = {handleMouseDownPassword}
          edge        = 'end'
        >
          {showPassword ? <Visibility /> : <VisibilityOff />}
        </IconButton>
      </InputAdornment>
    )
  }


  //#endregion

  return display();
}
